import { DateTime } from "luxon";

import { EmployeeRegistryClient } from "@/Common/Api/Employee/EmployeeRegistryClient";
import { ExternalEmployeeResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/ExternalEmployeeResponse";
import { ExternalServiceClient } from "@/Common/Api/ExternalServiceClient";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";

interface ISearchExternalEmployeeControlProps {
    componentId: string;
    selectedEmployees: ExternalEmployeeResponse[];
    includeSearchFromSameClient?: boolean;
    onSelected?: (employee: ExternalEmployeeResponse) => void;
    onClear?: (employee?: ExternalEmployeeResponse) => void;

    maxSelection?: number;
    disabled?: boolean;
    focusOnLoad?: boolean;

    valid?: boolean;
    useblurevent?: boolean;
    closeDropDownAfterSelection?: boolean;
    defaultTextValue?: string;
}

class SearchExternalEmployeeControlState {
    public searchText: string;
    public expanded: boolean;
    public infocus: boolean;
    public dropdownItemList: ExternalEmployeeDropdownItem[];
}

class SearchExternalEmployeeControl extends BaseReactComponent<ISearchExternalEmployeeControlProps, SearchExternalEmployeeControlState> {
    private width: string = "";
    private debounceMs: number = 400;
    private blurBuffer: number;
    private timeoutReference: any;
    private inputControlId: string;
    private employeeRegClient: ExternalServiceClient;

    constructor(props: ISearchExternalEmployeeControlProps) {
        super(props);

        this.inputControlId = "input_" + props.componentId;
        this.employeeRegClient = EmployeeRegistryClient.getInstanceV1();

        this.state = this.prepareStateForNewRuleSetProps(new SearchExternalEmployeeControlState(), props);
    }

    public componentDidUpdate(prevProps: ISearchExternalEmployeeControlProps): void {
        if (this.props !== prevProps)
            this.mutateState(s => this.prepareStateForNewRuleSetProps(s, this.props));
    }

    public prepareStateForNewRuleSetProps(s: SearchExternalEmployeeControlState, props: ISearchExternalEmployeeControlProps): SearchExternalEmployeeControlState {
        s.expanded = false;
        s.searchText = "";
        s.dropdownItemList = [];

        s.infocus = props.focusOnLoad === true;

        if (props.defaultTextValue?.length > 0) // allow for a default text value in text input
            s.searchText = props.defaultTextValue;

        return s;
    }

    public componentDidMount(): void {
        this.width = ($("#" + this.props.componentId).width() + 10) + "px";

        if (this.props.useblurevent == null || this.props.useblurevent === false)
            $(window).on("click.pc", () => this.onWindowClick());

        if (this.props.focusOnLoad)
            this.rawSetFocus();

        if (!IsNullOrWhiteSpace(this.props.defaultTextValue)) {
            window.setTimeout(
                () => this.executeSearch(this.props.defaultTextValue),
                200
            );
        }
    }

    public render(): JSX.Element {
        const expand = this.state.expanded && this.state.dropdownItemList.length > 0;

        let css = this.props.valid === false ? "patient-autocomplete invalid" : "patient-autocomplete";
        if (this.props.disabled)
            css += " disabled";

        return (
            <div id={this.props.componentId} className={css} onClick={e => this.onContainerClick(e)}>
                <div className="flex-container agrb">
                    {this.props.selectedEmployees.map((employee, idx) => (
                        <div key={idx} data-clinic={employee.ClientTag} className="selected-item flex-container" onClick={e => e.stopPropagation()}>
                            <span className="content-text">{employee.displayName}</span>
                            <span className="remove-btn" onClick={e => this.removeItem(employee, e)} />
                        </div>
                    ))}
                    {(!this.props.maxSelection || this.props.selectedEmployees.length < this.props.maxSelection) && (
                        <div className="patient-search-field">
                            <input
                                tabIndex={0}
                                id={this.inputControlId}
                                onKeyDown={e => this.inputKeyDown(e)}
                                onBlur={() => this.onBlur()}
                                onChange={e => this.searchTextChanged(e)}
                                disabled={this.props.disabled}
                                value={this.state.searchText}
                                type="text"
                            />
                        </div>
                    )}

                </div>
                <div className="pr">
                    {expand && (
                        <div style={{ width: this.width, overflowX: "hidden", whiteSpace: "nowrap", zIndex: 2000 }} className="ui-wrp-b">
                            <ul data-role="available-person">
                                {this.state.dropdownItemList.map((item, idx) => {
                                    const isSelected = this.props.selectedEmployees.some(d => d.EmployeeId === item.Model.EmployeeId);
                                    const css = `${isSelected ? "selected" : ""} ${item.Active ? "pending" : ""}`.trim();
                                    const prefix = cacheStore.GetLocalizedValueByLookupId(item.Model.PrefixLookupId);

                                    return (
                                        <li id={"li_person_" + idx} data-clinic={item.Model.ClientTag} className={css} key={idx} style={{ padding: "5px" }} onClick={e => this.onItemClick(item, e)}>
                                            <div className="flex-container">
                                                <h5 style={{ fontWeight: "bold" }}>{prefix ? `${prefix} ` : ""}{item.Model.LastName}, {item.Model.FirstName}{item.Model.LicenseNumber ? ` (${item.Model.LicenseNumber})` : ""}</h5>
                                                {item.Model.LastLogin && this.isEmployeeInactiveMoreThan30Days(item.Model.LastLogin) &&
                                                    <h5>{cacheStore.GetString("InactiveLabel")} {cacheStore.GetString("SinceLabel").toLocaleLowerCase()}: {this.getEmployeeInactiveLabel(item.Model.LastLogin)}</h5>
                                                }
                                            </div>
                                            <h5>{item.Model.OrganizationName ? `${item.Model.OrganizationName} - ` : ""}{item.Model.SiteName}</h5>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    public componentWillUnmount(): void {
        clearTimeout(this.blurBuffer);
        if (this.props.useblurevent == null || this.props.useblurevent === false) {
            $(window).off("click.pc");
        }
    }

    private showInputControl(): void {
        this.mutateState(
            s => {
                s.infocus = true;
                s.expanded = true;
            },
            () => this.rawSetFocus()
        );
    }

    private rawSetFocus(): void {
        $("#" + this.inputControlId).focus();
    }

    private onBlur(): void {
        if (this.props.useblurevent == null || this.props.useblurevent === true) {
            this.onControlLeave();
        }
    }

    private onContainerClick(e: React.MouseEvent): void {
        e.stopPropagation();
        this.showInputControl();

        if (IsNullOrWhiteSpace(this.state.searchText)) {
            this.executeSearch("");
        }
    }

    private executeSearch(searchTxt: string): void {
        if (!searchTxt)
            return;

        this.employeeRegClient.get(
            `Search/${searchTxt}?includeOriginClient=${this.props.includeSearchFromSameClient || false}`,
            null,
            employees => {
                if (!employees || !Array.isArray(employees))
                    return;

                const externalEmployee = employees.map(x => ExternalEmployeeResponse.fromEmployeeRegResponse(x));
                this.handleSearchResults(externalEmployee.map(x => new ExternalEmployeeDropdownItem(x)));
            }
        );
    }

    private onWindowClick(): void {
        this.mutateState(s => {
            if (!isArrayNullOrUndefinedOrEmpty(this.props.selectedEmployees))
                s.infocus = false;
            s.expanded = false;
        });
    }

    private onControlLeave(): void {
        this.blurBuffer = window.setTimeout(
            () => this.mutateState(s => {
                if (!isArrayNullOrUndefinedOrEmpty(this.props.selectedEmployees))
                    s.infocus = false;
                s.expanded = false;
            }),
            300
        );
    }
    private inputKeyDown(e: React.KeyboardEvent): void {
        if (e.keyCode === 8) {
            // delete last item
            if (e.ctrlKey) {
                if (this.state.searchText.length === 0) {
                    this.props.onClear?.();
                }
            }
            else {
                if (this.props.selectedEmployees.length > 0 && this.state.searchText.length === 0) {
                    this.props.onClear?.(this.props.selectedEmployees[this.props.selectedEmployees.length - 1]);
                }
            }
        }

        // up
        if (e.keyCode === 38) { e.preventDefault(); this.previousItem(); }

        // down
        if (e.keyCode === 40) { e.preventDefault(); this.nextItem(); }

        // enter
        if (e.keyCode === 13) { this.addSelectedItem(); }

        // escape
        if (e.keyCode === 27) {
            this.clearState();
        }
    }

    // keyboard triggered
    private nextItem(): void {
        const collection = this.state.dropdownItemList.toImmutable();
        const idx = this.indexOfSelectedItem();

        if (idx === -1)
            return;

        if (idx + 1 < collection.size) {
            collection.get(idx).Active = false;
            collection.get(idx + 1).Active = true;
            this.updateStateWithNewPersonList(collection.toArray(), true);
        }

        this.adjustScoll();
    }

    private adjustScoll(): void {
        const idx = this.indexOfSelectedItem();
        const container = $("#" + this.props.componentId);
        const scrollParent = container.find("div.ui-wrp-b");
        const ul = container.find('ul[data-role="available-person"]');
        const liSelected = ul.find("#li_person_" + idx);
        const firstItem = ul.find("#li_person_" + 0);

        if (scrollParent != null && liSelected != null && firstItem != null) {
            const scrollParentHeight = scrollParent.height();
            const currentScrollPosition = Math.abs(firstItem.position().top);

            const selectedItemRelativePosition = liSelected.position().top; // relative to CurrentScrollPosition
            const selectedItemHeight = liSelected.height();

            if (selectedItemRelativePosition < 0) // Item is above scroll position
                scrollParent.scrollTop(currentScrollPosition + selectedItemRelativePosition);
            else if (selectedItemRelativePosition + selectedItemHeight > scrollParentHeight) // Item is below scroll
                scrollParent.scrollTop(currentScrollPosition + (selectedItemRelativePosition + selectedItemHeight - scrollParentHeight)); // Add the offset to scroll position in order to fully include the last item
        }
    }

    // keyboard triggered
    private previousItem(): void {
        let idx = this.indexOfSelectedItem();
        if (idx === -1) {
            this.collapseDropdown();
            return;
        }

        const collection = this.state.dropdownItemList.toImmutable();
        if (idx === 0) {
            collection.get(0).Active = false;
            this.updateStateWithNewPersonList(collection.toArray(), false);
            return;
        }

        if (idx > 0) {
            collection.get(idx).Active = false;
            idx--;
            collection.get(idx).Active = true;
            this.updateStateWithNewPersonList(collection.toArray(), true);
        }

        else if (idx === 1) {
            collection.get(0).Active = true;
            collection.get(1).Active = false;
            this.updateStateWithNewPersonList(collection.toArray(), false);
        }

        this.adjustScoll();
    }

    // keyboard triggered
    private addSelectedItem(): void {
        const idx = this.indexOfSelectedItem();
        if (idx >= 0) {
            this.props.onSelected && this.props.onSelected(this.state.dropdownItemList[idx].Model);

            this.clearQueryString();

            if (this.props.closeDropDownAfterSelection)
                this.clearState();
        }
    }

    private updateStateWithNewPersonList(list: ExternalEmployeeDropdownItem[], expand: boolean): void {
        this.mutateState(s => {
            s.expanded = expand;
            s.dropdownItemList = list;
        });
    }

    // returns index of item in state.patients array of active item
    private indexOfSelectedItem(): number {
        if (this.state.dropdownItemList.length === 0)
            return -1;

        for (let i = 0; i < this.state.dropdownItemList.length; i++) {
            if (this.state.dropdownItemList[i].Active)
                return i;
        }

        return -1;
    }

    private onItemClick(item: ExternalEmployeeDropdownItem, e: React.MouseEvent): void {
        e.stopPropagation();
        if (this.blurBuffer != null) {
            clearTimeout(this.blurBuffer);
            this.blurBuffer = null;
        }

        this.props.onSelected && this.props.onSelected(item.Model);

        this.clearQueryString();

        if (this.props.closeDropDownAfterSelection)
            this.clearState();
    }

    private removeItem(item: ExternalEmployeeResponse, e: React.MouseEvent): void {
        e.stopPropagation();
        this.clearQueryString();
        this.props.onClear && this.props.onClear(item);
    }

    private searchTextChanged(e: React.ChangeEvent<HTMLInputElement>): void {
        if (this.timeoutReference != null) {
            clearTimeout(this.timeoutReference);
            this.timeoutReference = null;
        }

        const searchText = e.target.value;

        this.mutateState(s => s.searchText = searchText);

        this.timeoutReference = window.setTimeout(
            () => this.executeSearch(searchText),
            this.debounceMs
        );
    }

    private collapseDropdown(): void {
        this.changeDropdown(false);
    }

    private changeDropdown(expand: boolean): void {
        this.mutateState(s => s.expanded = expand);
    }

    private clearState(): void {
        this.mutateState(s => {
            s.expanded = false;
            s.infocus = false;
            s.dropdownItemList = [];
            s.searchText = "";
        });
    }

    private clearQueryString(): void {
        this.mutateState(s => s.searchText = "", () => this.showInputControl());
    }

    private handleSearchResults(searchResults: ExternalEmployeeDropdownItem[]): void {
        if (!searchResults) return;

        this.updateStateWithNewPersonList(searchResults, true);
    }

    private getEmployeeInactiveDays(lastLogin: DateTime): number {
        return Math.floor(lastLogin.diffNow("days").days * -1);
    }

    private getEmployeeInactiveMonths(lastLogin: DateTime): number {
        return Math.floor(lastLogin.diffNow("months").months * -1);
    }

    private getEmployeeInactiveLabel(lastLogin: DateTime): string {
        const inactiveDays = this.getEmployeeInactiveDays(lastLogin);
        const ninetyNineDays = 99;

        if (inactiveDays <= ninetyNineDays)
            return `${inactiveDays} ${cacheStore.GetString("DaysLabel").toLocaleLowerCase()}`;

        const inactiveMonths = this.getEmployeeInactiveMonths(lastLogin);

        return `${inactiveMonths} ${cacheStore.GetString("MonthsLabel").toLocaleLowerCase()}`;
    }

    private isEmployeeInactiveMoreThan30Days(date: DateTime): boolean {
        const thirtyDays = 30;
        return this.getEmployeeInactiveDays(date) > thirtyDays;
    }
}

class ExternalEmployeeDropdownItem {
    public Model: ExternalEmployeeResponse;
    public Active: boolean;

    constructor(model?: ExternalEmployeeResponse) {
        this.Model = model;
        this.Active = false;
    }
}

export { SearchExternalEmployeeControl };
